import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { RouteProp, useRoute } from '@react-navigation/native';

import { useGetEntryDetailsQuery } from '@/api/entries/query.generated';
import { useEventsInfoQuery } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import Winner from '@/assets/icons/winner';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { playersHaveEdgeCombo } from '@/feature/betslip-pickem/utils/betslip-edge-combos';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { createPlayerEventKey } from '@/feature/betslip-pickem/utils/markets-cache-store-utils';
import { EntryPickPlayerCard } from '@/feature/entries-pickem/components/EntryPickPlayerCard';
import { ShareEntryProvider } from '@/feature/entries-pickem/components/ShareEntryProvider';
import { getEventIdsFromPicks, isEntryWin } from '@/feature/entries-pickem/entries-utils';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { RootStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';
import { logger } from '@/utils/logging';
import { groupPicksByEvents } from '@/utils/map-to-events';
import { format } from 'date-fns';

import { useFantasyEntryShare } from '../hooks/use-fantasy-entry-share';
import { useShareEntryCapture } from '../hooks/use-share-entry-capture';
import { PicksShareEntryHeader } from './PicksShareEntryHeader';

export const SHARE_SCREEN_BG_COLOR = ds.colorValue.background.neutral.base;

type EntryShareRouteProp = RouteProp<RootStackParamList, 'EntryShare'>;

export const EntryShare = () => (
    <ShareEntryProvider>
        <EntryShareContent />
    </ShareEntryProvider>
);

const EntryShareContent = () => {
    const viewRef = useRef<View>(null);
    const imageCaptured = useRef(false);
    const route = useRoute<EntryShareRouteProp>();
    const { id } = route.params;
    const reuseShareLineupEnabled = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.settings.enable_share_and_reuse_lineups
    );
    const { t } = useTranslation('pickem_share_entry_modal');
    const errorToast = t('sharing_failed_msg');
    const [eventsMap, setEventsMap] = useState<Record<string, EventInfo>>({});
    const [numberOfLoadedImages, setNumberOfLoadedImages] = useState(0);
    const [timerPassed, setTimerPassed] = useState(false);

    const [{ data, fetching }] = useGetEntryDetailsQuery({
        variables: { id: id ?? '' },
        requestPolicy: 'network-only',
    });

    const entry = data?.getEntry;
    const sections = useMemo(
        () => groupPicksByEvents(entry?.picks ?? [], entry?.edgeCombos ?? []),
        [entry?.edgeCombos, entry?.picks]
    );

    const eventIds = getEventIdsFromPicks(entry?.picks);
    const [{ data: eventsInfoData }, executeEventsInfoQuery] = useEventsInfoQuery({
        requestPolicy: 'cache-and-network',
        variables: {
            ids: eventIds,
        },
        pause: true,
    });
    const winner = isEntryWin(entry);

    useEffect(() => {
        if (!eventsInfoData?.getEventsByIdsV2?.length) {
            return;
        }

        setEventsMap(eventsInfoData.getEventsByIdsV2.reduce((acc, event) => ({ ...acc, [event.id]: event }), {}));
    }, [eventsInfoData?.getEventsByIdsV2]);
    useEffect(() => {
        if (!entry) {
            return;
        }
        executeEventsInfoQuery();
    }, [entry, executeEventsInfoQuery]);

    const { showToast } = useAlerts();
    const numberOfPlayers = sections.reduce((acc, section) => acc + section.data.length, 0);
    const areAllImagesLoaded = numberOfPlayers === numberOfLoadedImages || timerPassed;

    const { captureShareImage, presentShareLink, removeShareScreen } = useShareEntryCapture(viewRef);
    const { createFantasyShareLink } = useFantasyEntryShare();

    const handleImageLoad = useCallback(() => {
        setNumberOfLoadedImages(prev => prev + 1);
    }, []);

    useEffect(() => {
        // Start a 3-second timer to show the share modal and not letting user wait for too long
        if (entry) {
            const timer = setTimeout(() => {
                setTimerPassed(true);
            }, 2000);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [entry]);

    const capture = useCallback(async () => {
        try {
            //Do not capture and create the shareable link if the feature is disabled
            //and users bypassed somehow this, and ended up on this screen
            if (!reuseShareLineupEnabled) {
                return;
            }

            const screenshot = await captureShareImage();

            if (entry) {
                presentShareLink(screenshot, await createFantasyShareLink(entry));
            }
        } catch (e) {
            logger.warn('EntryShare', e);
            showToast({ message: errorToast, toastType: 'warning' });
            removeShareScreen();
        }
    }, [
        captureShareImage,
        createFantasyShareLink,
        entry,
        errorToast,
        presentShareLink,
        removeShareScreen,
        reuseShareLineupEnabled,
        showToast,
    ]);

    useEffect(() => {
        if (entry && eventsMap && !imageCaptured?.current && areAllImagesLoaded) {
            imageCaptured.current = true;
            setTimeout(() => {
                capture();
            }, 100);
        } else if (!fetching && !entry) {
            removeShareScreen();
            showToast({ message: errorToast, toastType: 'warning' });
        }
    }, [capture, entry, errorToast, eventsMap, fetching, removeShareScreen, showToast, areAllImagesLoaded]);

    return (
        <SafeAreaView style={common.flex} edges={['top']}>
            <ScrollView
                showsVerticalScrollIndicator={showScrollIndicator}
                contentContainerStyle={fetching ? common.flex : null}
            >
                <MaxWidthWrapper>
                    <Box flex={1} style={styles.wrapper} ref={viewRef}>
                        {fetching ? (
                            <Box style={styles.loaderWrapper}>
                                <ActivityIndicator size={'large'} />
                            </Box>
                        ) : (
                            <View collapsable={false} style={isWeb ? styles.contentContainer : null}>
                                {winner ? (
                                    <Box style={common.absolute}>
                                        <Winner />
                                    </Box>
                                ) : null}
                                <PicksShareEntryHeader entry={entry} />
                                <View style={styles.container}>
                                    <View>
                                        {/* using map with ScrollView instead of SectionList because it doesn't capture the entire screen if there is scroll */}
                                        {sections.map(section =>
                                            section.data.map((item, index) => {
                                                const isFirst = index === 0;
                                                const isLast = index + 1 === section.data.length;
                                                const nextPlayer = section.data[index + 1];
                                                const isEdgeCombo = playersHaveEdgeCombo(
                                                    entry?.edgeCombos ?? [],
                                                    item.player,
                                                    nextPlayer?.player
                                                );
                                                const event = eventsMap[item.eventId];
                                                const team = eventUtils.getTeamById(event, item.teamId);

                                                return (
                                                    <EntryPickPlayerCard
                                                        key={createPlayerEventKey(item.player.id, item.eventId)}
                                                        team={team}
                                                        eventLeague={event?.league}
                                                        animateProgressBars={false}
                                                        hideCrossedOutValue
                                                        isLast={isLast}
                                                        isFirst={isFirst}
                                                        {...item}
                                                        isEdgeCombo={isEdgeCombo}
                                                        isShareableContent={true}
                                                        backgroundColor={ds.colorValue.background.neutral.subtler}
                                                        borderColor={ds.colorValue.border.subtle}
                                                        imageBackgroundColor={ds.colorToken.background.neutral.subtle}
                                                        onImageLoad={handleImageLoad}
                                                    />
                                                );
                                            })
                                        )}
                                        <Box>
                                            <Text
                                                variant="bodySmaller"
                                                style={styles.createdDate}
                                                textAlign={'center'}
                                                letterSpacing={-0.2}
                                                lineHeight={16}
                                                marginBottom={'s16'}
                                            >
                                                {`${t('placed')} ${
                                                    entry?.createdDate && Date.parse(entry?.createdDate)
                                                        ? format(
                                                              Date.parse(entry?.createdDate),
                                                              'MMM dd, yyyy @ hh:mm aa'
                                                          )
                                                        : entry?.createdDate
                                                }`}
                                            </Text>
                                        </Box>
                                    </View>
                                </View>
                            </View>
                        )}
                    </Box>
                </MaxWidthWrapper>
            </ScrollView>
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        backgroundColor: SHARE_SCREEN_BG_COLOR,
    },
    container: {
        paddingHorizontal: 16,
    },
    loaderWrapper: {
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    contentContainer: {
        position: 'absolute',
        width: '100%',
    },
    createdDate: {
        color: ds.colorValue.text.primary,
        opacity: 0.4,
    },
});
